import type { ReactNode } from 'react';
import { Icon, type IconName } from '../icons/Icon.js';
import { cx } from '../utils/cx.js';

/**
 * 画面内の告知（C-13 の Web 版バナー・入力期限の警告・提出済みの説明など）。
 * tone は色を使う 3 家族 + 無色。色だけで伝えず、必ず文言を添える。
 */
export type NoticeTone = 'neutral' | 'warning' | 'danger' | 'success';

const ICON: Record<NoticeTone, IconName> = { neutral: 'info', warning: 'warning', danger: 'error', success: 'check_circle' };
const CLS: Record<NoticeTone, string> = {
  neutral: 'border-line-strong text-fg',
  warning: 'border-warning text-fg [&>svg]:text-warning',
  danger: 'border-danger text-fg [&>svg]:text-danger',
  success: 'border-success text-fg [&>svg]:text-success',
};

export interface NoticeProps {
  tone?: NoticeTone;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
  icon?: IconName | null;
  /** 読み上げ（エラーは assertive） */
  live?: boolean;
}

export function Notice({ tone = 'neutral', title, children, actions, className, icon, live }: NoticeProps) {
  const iconName = icon === undefined ? ICON[tone] : icon;
  return (
    <div
      role={live ? (tone === 'danger' ? 'alert' : 'status') : undefined}
      className={cx('flex items-start gap-3 rounded-input border bg-card px-4 py-3', CLS[tone], className)}
    >
      {iconName ? <Icon name={iconName} size={24} className="mt-0.5 shrink-0" /> : null}
      <div className="min-w-0 flex-1">
        {title ? <p className="text-callout font-semibold">{title}</p> : null}
        {children ? <div className="text-callout text-fg-secondary">{children}</div> : null}
        {actions ? <div className="mt-2 flex flex-wrap gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
